import ResponsiveTable from './ResponsiveTable';
import React, { useEffect, useState } from 'react';
import { dashboardAPI } from '../services/api';
import api from '../services/api';
import InvestmentsPortfolio from './InvestmentsPortfolio';

interface Transaction {
  id: number;
  type: 'income' | 'expense';
  amount: number;
  category: string;
  description: string | null;
  date: string;
}

interface Category {
  id: number;
  name: string;
  type: 'income' | 'expense' | 'both';
  icon: string;
  color: string;
  isDefault: boolean;
}

const Finances: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [importing, setImporting] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [activeTab, setActiveTab] = useState<'transactions' | 'analytics' | 'investments'>('transactions');
  
  // Форма
  const [formData, setFormData] = useState({
    type: 'expense' as 'income' | 'expense',
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  // Форма новой категории
  const [newCategory, setNewCategory] = useState({
    name: '',
    type: 'expense' as 'income' | 'expense' | 'both',
    icon: '📦',
    color: '#3b82f6'
  });

  useEffect(() => {
    loadTransactions();
    loadCategories();
  }, []);

  const loadTransactions = async () => {
    try {
      const response = await api.get('/api/transactions');
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/api/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Обновление
        await api.put(`/api/transactions/${editingId}`, {
          ...formData,
          amount: parseFloat(formData.amount)
        });
      } else {
        // Создание
        await api.post('/api/transactions', {
          ...formData,
          amount: parseFloat(formData.amount)
        });
      }
      
      // Сбросить форму
      setFormData({
        type: 'expense',
        amount: '',
        category: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      setShowForm(false);
      setEditingId(null);
      loadTransactions();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при сохранении');
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setFormData({
      type: transaction.type,
      amount: transaction.amount.toString(),
      category: transaction.category,
      description: transaction.description || '',
      date: transaction.date
    });
    setEditingId(transaction.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить эту транзакцию?')) return;
    
    try {
      await api.delete(`/api/transactions/${id}`);
      loadTransactions();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при удалении');
    }
  };

  const handleCancel = () => {
    setFormData({
      type: 'expense',
      amount: '',
      category: '',
      description: '',
      date: new Date().toISOString().split('T')[0]
    });
    setShowForm(false);
    setEditingId(null);
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/categories', newCategory);
      setNewCategory({
        name: '',
        type: 'expense',
        icon: '📦',
        color: '#3b82f6'
      });
      setShowCategoryForm(false);
      loadCategories();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при создании категории');
    }
  };

  // Фильтруем категории по типу транзакции
  const filteredCategories = categories.filter(
    cat => cat.type === formData.type || cat.type === 'both'
  );

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/api/import-export/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      let message = `✅ Импортировано ${response.data.imported} транзакций!`;
      
      if (response.data.errors > 0) {
        message += `\n\n⚠️ Найдено ${response.data.errors} записей с неполными данными.\nПроверьте список ниже и отредактируйте их.`;
        
        // Показываем детали ошибок
        if (response.data.errorDetails && response.data.errorDetails.length > 0) {
          const errorSummary = response.data.errorDetails.slice(0, 5).map((err: any) => 
            `Строка ${err.row}: ${err.error}`
          ).join('\n');
          
          if (response.data.errorDetails.length > 5) {
            message += `\n\nПримеры проблем:\n${errorSummary}\n... и еще ${response.data.errorDetails.length - 5}`;
          } else {
            message += `\n\nПроблемы:\n${errorSummary}`;
          }
        }
      }
      
      alert(message);
      loadTransactions();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Ошибка при импорте файла');
    } finally {
      setImporting(false);
      e.target.value = ''; // Сбросить input
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/api/import-export/export', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      alert('Ошибка при экспорте файла');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/import-export/template', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_transactions.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      alert('Ошибка при скачивании шаблона');
    }
  };

  const handleClearAll = async () => {
    const confirmed = window.confirm(
      `⚠️ ВНИМАНИЕ!\n\nВы действительно хотите удалить ВСЕ транзакции?\n\nБудет удалено: ${transactions.length} записей\n\nЭто действие невозможно отменить!`
    );
    
    if (!confirmed) return;

    const doubleConfirm = window.confirm(
      '🔴 ПОСЛЕДНЕЕ ПРЕДУПРЕЖДЕНИЕ!\n\nВы точно уверены? Все данные будут безвозвратно удалены!'
    );

    if (!doubleConfirm) return;

    try {
      // Используем bulk delete эндпоинт
      const response = await api.delete('/api/transactions/bulk/all');
      
      alert(`✅ Успешно удалено ${response.data.deleted} транзакций`);
      loadTransactions();
    } catch (error: any) {
      alert('Ошибка при удалении транзакций');
    }
  };

  // Вычисляем статистику
  const totalIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0);

  const totalExpense = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0);

  const balance = totalIncome - totalExpense;

  if (loading) return <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <h1>💰 Финансы</h1>
      </div>

      {/* Вкладки */}
      <div style={{ 
        display: 'flex', 
        gap: '5px', 
        marginBottom: '20px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <button
          onClick={() => setActiveTab('transactions')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'transactions' ? '3px solid #16a34a' : '3px solid transparent',
            color: activeTab === 'transactions' ? '#16a34a' : '#6b7280',
            fontWeight: activeTab === 'transactions' ? 'bold' : 'normal',
            fontSize: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          📝 Транзакции
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'analytics' ? '3px solid #16a34a' : '3px solid transparent',
            color: activeTab === 'analytics' ? '#16a34a' : '#6b7280',
            fontWeight: activeTab === 'analytics' ? 'bold' : 'normal',
            fontSize: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          📊 Аналитика
        </button>
        <button
          onClick={() => setActiveTab('investments')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'investments' ? '3px solid #16a34a' : '3px solid transparent',
            color: activeTab === 'investments' ? '#16a34a' : '#6b7280',
            fontWeight: activeTab === 'investments' ? 'bold' : 'normal',
            fontSize: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            marginBottom: '-2px'
          }}
        >
          💎 Инвестиции
        </button>
      </div>

      {/* Контент вкладки Транзакции */}
      {activeTab === 'transactions' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <button
              onClick={handleDownloadTemplate}
              style={{
                padding: '10px 20px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              📥 Скачать шаблон
            </button>
            
            <label style={{
              padding: '10px 20px',
              background: importing ? '#9ca3af' : '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: importing ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              display: 'inline-block'
            }}>
            {importing ? '⏳ Импорт...' : '📤 Импорт из Excel'}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleImport}
              disabled={importing}
              style={{ display: 'none' }}
            />
          </label>

          <button
            onClick={handleExport}
            style={{
              padding: '10px 20px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            📊 Экспорт в Excel
          </button>

          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              padding: '10px 20px',
              background: showForm ? '#dc2626' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {showForm ? '✕ Отмена' : '+ Добавить'}
          </button>

          <button
            onClick={handleClearAll}
            style={{
              padding: '10px 20px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              marginLeft: 'auto'
            }}
            title="Удалить все транзакции (для отладки)"
          >
            🗑️ Очистить всё
          </button>
        </div>

        {/* Форма добавления/редактирования */}
        {showForm && (
        <div style={{ 
          background: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
          marginBottom: '20px' 
        }}>
          <h3>{editingId ? 'Редактировать транзакцию' : 'Новая транзакция'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Тип</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'income' | 'expense' })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  required
                >
                  <option value="income">Доход</option>
                  <option value="expense">Расход</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Сумма</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Категория</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    required
                  >
                    <option value="">Выберите категорию</option>
                    {filteredCategories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowCategoryForm(!showCategoryForm)}
                    style={{
                      padding: '8px 16px',
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {showCategoryForm ? '✕' : '+ Новая'}
                  </button>
                </div>
                
                {/* Форма новой категории */}
                {showCategoryForm && (
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '15px', 
                    background: '#f9fafb', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '4px' 
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Создать категорию</h4>
                    
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '500' }}>Название</label>
                      <input
                        type="text"
                        placeholder="Например: Кафе, Такси, Зарплата..."
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
                      />
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '500' }}>Иконка</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{
                          fontSize: '32px',
                          padding: '8px',
                          border: '2px solid #d1d5db',
                          borderRadius: '8px',
                          background: 'white',
                          width: '56px',
                          height: '56px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {newCategory.icon}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowIconPicker(!showIconPicker)}
                          style={{
                            padding: '8px 16px',
                            background: showIconPicker ? '#dc2626' : '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          {showIconPicker ? '✕ Закрыть' : '🎨 Выбрать иконку'}
                        </button>
                        <input
                          type="text"
                          placeholder="Или введите: 🎯"
                          value={newCategory.icon}
                          onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                          style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
                        />
                      </div>
                      
                      {showIconPicker && (
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(10, 1fr)', 
                          gap: '5px', 
                          padding: '10px',
                          background: 'white',
                          border: '2px solid #3b82f6',
                          borderRadius: '8px',
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          {['💰', '🏠', '🚗', '🍔', '⚡', '🎮', '👕', '🏥', '🎓', '✈️', 
                            '📱', '🎬', '🏋️', '🐕', '💊', '🎨', '📚', '🚕', '☕', '🍕',
                            '🛒', '💻', '🎵', '🌐', '📦', '🎁', '💳', '🏦', '📊', '💼',
                            '🔧', '🏪', '🚌', '⛽', '🍜', '🎯', '💡', '📞', '🎤', '🎸',
                            '🎹', '🎮', '⚽', '🏀', '🎾', '🏐', '🏈', '⚾', '🏓', '🏸',
                            '🥊', '🥋', '⛷️', '🏂', '⛳', '🎣', '🤿', '🎿', '🛼', '🛹'].map(icon => (
                            <button
                              key={icon}
                              type="button"
                              onClick={() => {
                                setNewCategory({ ...newCategory, icon });
                                setShowIconPicker(false);
                              }}
                              style={{
                                padding: '8px',
                                fontSize: '20px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                background: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#dbeafe'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                              title={icon}
                            >
                              {icon}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '500' }}>Тип</label>
                      <select
                        value={newCategory.type}
                        onChange={(e) => setNewCategory({ ...newCategory, type: e.target.value as 'income' | 'expense' | 'both' })}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
                      >
                        <option value="expense">Расход</option>
                        <option value="income">Доход</option>
                        <option value="both">Доход и Расход</option>
                      </select>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '500' }}>Цвет</label>
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <input
                          type="color"
                          value={newCategory.color}
                          onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                          style={{ width: '60px', height: '36px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                        />
                        <div style={{ 
                          padding: '8px 12px', 
                          background: newCategory.color, 
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: '500',
                          flex: 1,
                          textAlign: 'center'
                        }}>
                          {newCategory.icon} {newCategory.name || 'Предпросмотр'}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      style={{
                        padding: '8px 16px',
                        background: '#16a34a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      ✓ Создать категорию
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Дата</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  required
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Описание (необязательно)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  placeholder="Дополнительная информация..."
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  background: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {editingId ? 'Сохранить' : 'Добавить'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: '10px 20px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '15px', marginBottom: '30px' }}>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#666' }}>Баланс</h3>
          <p style={{ fontSize: '28px', fontWeight: 'bold', color: balance >= 0 ? '#16a34a' : '#dc2626', margin: 0 }}>
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(balance)}
          </p>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#666' }}>Доходы</h3>
          <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a', margin: 0 }}>
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(totalIncome)}
          </p>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#666' }}>Расходы</h3>
          <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#dc2626', margin: 0 }}>
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(totalExpense)}
          </p>
        </div>
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#666' }}>Транзакций</h3>
          <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#3b82f6', margin: 0 }}>
            {transactions.length}
          </p>
        </div>
      </div>

      {/* Transactions List */}
      <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2>Список транзакций</h2>
        {transactions.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
            Нет транзакций. Добавьте первую транзакцию!
          </p>
        ) : (
          <ResponsiveTable style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '10px', textAlign: 'left' }}>Дата</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Категория</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Описание</th>
                <th style={{ padding: '10px', textAlign: 'right' }}>Сумма</th>
                <th style={{ padding: '10px', textAlign: 'center' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => (
                <React.Fragment key={transaction.id}>
                  {editingId === transaction.id ? (
                    // Форма редактирования на месте
                    <tr style={{ 
                      backgroundColor: '#f0f9ff',
                      border: '2px solid #3b82f6'
                    }}>
                      <td colSpan={5} style={{ padding: '15px' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'end', flexWrap: 'wrap' }}>
                          <div style={{ flex: '0 0 140px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#666' }}>Дата</label>
                            <input
                              type="date"
                              value={formData.date}
                              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                            />
                          </div>

                          <div style={{ flex: '0 0 140px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#666' }}>Тип</label>
                            <select
                              value={formData.type}
                              onChange={(e) => setFormData({ ...formData, type: e.target.value as 'income' | 'expense' })}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="expense">Расход</option>
                              <option value="income">Доход</option>
                            </select>
                          </div>

                          <div style={{ flex: '1 1 200px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#666' }}>Категория</label>
                            <select
                              value={formData.category}
                              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                              style={{
                                flex: 1,
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px',
                                width: '100%'
                              }}
                            >
                              <option value="">Выберите категорию</option>
                              {categories
                                .filter(cat => cat.type === formData.type || cat.type === 'both')
                                .map(cat => (
                                  <option key={cat.id} value={cat.name}>{cat.icon} {cat.name}</option>
                                ))}
                            </select>
                          </div>

                          <div style={{ flex: '0 0 120px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#666' }}>Сумма</label>
                            <input
                              type="number"
                              value={formData.amount}
                              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                            />
                          </div>

                          <div style={{ flex: '1 1 250px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', color: '#666' }}>Описание</label>
                            <input
                              type="text"
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                              onClick={handleSubmit}
                              style={{
                                padding: '8px 16px',
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              💾 Сохранить
                            </button>
                            <button
                              onClick={() => {
                                setEditingId(null);
                                setFormData({
                                  type: 'expense',
                                  amount: '',
                                  category: '',
                                  description: '',
                                  date: new Date().toISOString().split('T')[0]
                                });
                              }}
                              style={{
                                padding: '8px 16px',
                                background: '#6b7280',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                              }}
                            >
                              ✖️ Отмена
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    // Обычная строка транзакции
                    <tr 
                      style={{ 
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: transaction.category === 'Не указана' ? '#fff5f5' : 'white',
                        borderLeft: transaction.category === 'Не указана' ? '3px solid #ff6b6b' : 'none'
                      }}
                    >
                      <td style={{ padding: '10px' }}>
                        {transaction.category === 'Не указана' && (
                          <span style={{ 
                            color: '#ff6b6b', 
                            marginRight: '5px',
                            fontSize: '14px'
                          }}>⚠️</span>
                        )}
                        {new Date(transaction.date).toLocaleDateString('ru-RU')}
                      </td>
                      <td style={{ padding: '10px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: transaction.category === 'Не указана' 
                            ? '#ffebee' 
                            : transaction.type === 'income' ? '#dcfce7' : '#fee2e2',
                          color: transaction.category === 'Не указана'
                            ? '#d32f2f'
                            : transaction.type === 'income' ? '#16a34a' : '#dc2626',
                          border: transaction.category === 'Не указана' ? '1px solid #ff6b6b' : 'none'
                        }}>
                          {transaction.category}
                          {transaction.category === 'Не указана' && ' 👈'}
                        </span>
                      </td>
                      <td style={{ padding: '10px', color: '#666' }}>{transaction.description || '—'}</td>
                      <td style={{ 
                        padding: '10px', 
                        textAlign: 'right',
                        color: transaction.type === 'income' ? '#16a34a' : '#dc2626',
                        fontWeight: 'bold'
                      }}>
                        {transaction.type === 'income' ? '+' : '-'}
                        {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(parseFloat(transaction.amount.toString()))}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleEdit(transaction)}
                          style={{
                            padding: '6px 12px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '5px',
                            fontSize: '12px'
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(transaction.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </ResponsiveTable>
        )}
      </div>
      </div>
      )}

      {/* Контент вкладки Аналитика */}
      {activeTab === 'analytics' && (
        <FinancialAnalytics transactions={transactions} />
      )}

      {/* Контент вкладки Инвестиции */}
      {activeTab === 'investments' && (
        <InvestmentsPortfolio />
      )}
    </div>
  );
};

// Компонент финансовой аналитики
const FinancialAnalytics: React.FC<{ transactions: Transaction[] }> = ({ transactions }) => {
  const [period, setPeriod] = useState<'month' | 'year' | 'all' | 'custom'>('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hoveredLineIndex, setHoveredLineIndex] = useState<number | null>(null);
  const [hoveredPieIndex, setHoveredPieIndex] = useState<number | null>(null);

  // Фильтрация по периоду
  const getFilteredTransactions = () => {
    const now = new Date();
    let filterStartDate: Date | null = null;

    if (period === 'month') {
      filterStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (period === 'year') {
      filterStartDate = new Date(now.getFullYear(), 0, 1);
    } else if (period === 'custom' && startDate && endDate) {
      return transactions.filter((t: Transaction) => {
        const tDate = new Date(t.date);
        return tDate >= new Date(startDate) && tDate <= new Date(endDate);
      });
    }

    if (filterStartDate && period !== 'custom') {
      return transactions.filter((t: Transaction) => new Date(t.date) >= filterStartDate!);
    }

    return transactions;
  };

  const filtered = getFilteredTransactions();

  // Подготовка данных для линейного графика
  const prepareLineData = () => {
    const grouped: { [date: string]: { income: number; expense: number } } = {};

    filtered.forEach((t: Transaction) => {
      const date = t.date;
      if (!grouped[date]) {
        grouped[date] = { income: 0, expense: 0 };
      }
      if (t.type === 'income') {
        grouped[date].income += parseFloat(t.amount.toString());
      } else {
        grouped[date].expense += parseFloat(t.amount.toString());
      }
    });

    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, data]) => ({
        date: new Date(date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
        income: data.income,
        expense: data.expense
      }));
  };

  // Подготовка данных для круговой диаграммы
  const preparePieData = () => {
    const categoryTotals: { [category: string]: number } = {};
    let totalExpense = 0;

    filtered.forEach((t: Transaction) => {
      if (t.type === 'expense') {
        const amount = parseFloat(t.amount.toString());
        categoryTotals[t.category] = (categoryTotals[t.category] || 0) + amount;
        totalExpense += amount;
      }
    });

    return Object.entries(categoryTotals)
      .map(([category, amount]) => ({
        category,
        amount,
        percentage: totalExpense > 0 ? (amount / totalExpense) * 100 : 0
      }))
      .sort((a, b) => b.amount - a.amount);
  };

  const lineData = prepareLineData();
  const pieData = preparePieData();

  const totalIncome = filtered
    .filter((t: Transaction) => t.type === 'income')
    .reduce((sum: number, t: Transaction) => sum + parseFloat(t.amount.toString()), 0);

  const totalExpense = filtered
    .filter((t: Transaction) => t.type === 'expense')
    .reduce((sum: number, t: Transaction) => sum + parseFloat(t.amount.toString()), 0);

  const balance = totalIncome - totalExpense;

  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
    '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
    '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
    '#ec4899', '#f43f5e'
  ];

  return (
    <div>
      {/* Выбор периода */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={() => setPeriod('month')}
          style={{
            padding: '10px 20px',
            background: period === 'month' ? '#16a34a' : 'white',
            color: period === 'month' ? 'white' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          📅 Месяц
        </button>

        <button
          onClick={() => setPeriod('year')}
          style={{
            padding: '10px 20px',
            background: period === 'year' ? '#16a34a' : 'white',
            color: period === 'year' ? 'white' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          📆 Год
        </button>

        <button
          onClick={() => setPeriod('all')}
          style={{
            padding: '10px 20px',
            background: period === 'all' ? '#16a34a' : 'white',
            color: period === 'all' ? 'white' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          🕐 Всё время
        </button>

        <button
          onClick={() => setPeriod('custom')}
          style={{
            padding: '10px 20px',
            background: period === 'custom' ? '#16a34a' : 'white',
            color: period === 'custom' ? 'white' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          📋 Свой период
        </button>

        {period === 'custom' && (
          <>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
            />
            <span style={{ color: '#6b7280' }}>—</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px' }}
            />
          </>
        )}
      </div>

      {/* Карточки статистики */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))', gap: '15px', marginBottom: '30px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>Доходы</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(totalIncome)}
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>Расходы</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(totalExpense)}
          </div>
        </div>

        <div style={{
          background: balance >= 0 
            ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
            : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          padding: '20px',
          borderRadius: '12px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>Баланс</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
            {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(balance)}
          </div>
        </div>
      </div>

      {/* Графики */}
      <div className="mobile-stack-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* График доходов/расходов */}
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <h3 style={{ marginTop: 0, fontSize: '18px', marginBottom: '20px' }}>📈 Доходы и расходы</h3>
          {lineData.length > 0 ? (
            <SimpleLineChart data={lineData} />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
              Нет данных за выбранный период
            </div>
          )}
        </div>

        {/* Круговая диаграмма */}
        <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <h3 style={{ marginTop: 0, fontSize: '18px', marginBottom: '20px' }}>🎯 Расходы по категориям</h3>
          {pieData.length > 0 ? (
            <SimplePieChart data={pieData} colors={colors} />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
              Нет расходов за выбранный период
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Простой линейный график
const SimpleLineChart: React.FC<{ data: Array<{ date: string; income: number; expense: number }> }> = ({ data }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const maxValue = Math.max(...data.map(d => Math.max(d.income, d.expense)), 1000);
  const chartHeight = 300;
  const chartWidth = 550;
  const padding = { top: 20, right: 20, bottom: 40, left: 60 };

  return (
    <div style={{ position: 'relative' }}>
      <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ overflow: 'visible' }}>
        {/* Сетка */}
        {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
          const y = padding.top + (chartHeight - padding.top - padding.bottom) * (1 - ratio);
          return (
            <g key={ratio}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e5e7eb" strokeDasharray="4" />
              <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="10" fill="#6b7280">
                {new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format(maxValue * ratio)}
              </text>
            </g>
          );
        })}

        {/* Линии */}
        {data.length > 1 && (
          <>
            <polyline
              points={data.map((d, i) => {
                const x = padding.left + (i / (data.length - 1)) * (chartWidth - padding.left - padding.right);
                const y = padding.top + (chartHeight - padding.top - padding.bottom) * (1 - d.income / maxValue);
                return `${x},${y}`;
              }).join(' ')}
              fill="none"
              stroke="#10b981"
              strokeWidth="3"
            />
            <polyline
              points={data.map((d, i) => {
                const x = padding.left + (i / (data.length - 1)) * (chartWidth - padding.left - padding.right);
                const y = padding.top + (chartHeight - padding.top - padding.bottom) * (1 - d.expense / maxValue);
                return `${x},${y}`;
              }).join(' ')}
              fill="none"
              stroke="#ef4444"
              strokeWidth="3"
            />
          </>
        )}

        {/* Точки */}
        {data.map((d, i) => {
          const x = padding.left + (i / Math.max(data.length - 1, 1)) * (chartWidth - padding.left - padding.right);
          const incomeY = padding.top + (chartHeight - padding.top - padding.bottom) * (1 - d.income / maxValue);
          const expenseY = padding.top + (chartHeight - padding.top - padding.bottom) * (1 - d.expense / maxValue);

          return (
            <g key={i}>
              <circle cx={x} cy={incomeY} r={hoveredIndex === i ? 6 : 4} fill="#10b981" 
                onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)} style={{ cursor: 'pointer' }} />
              <circle cx={x} cy={expenseY} r={hoveredIndex === i ? 6 : 4} fill="#ef4444" 
                onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)} style={{ cursor: 'pointer' }} />

              {hoveredIndex === i && (
                <g>
                  <rect x={x - 60} y={Math.min(incomeY, expenseY) - 60} width="120" height="50" fill="white" stroke="#d1d5db" rx="4" />
                  <text x={x} y={Math.min(incomeY, expenseY) - 42} textAnchor="middle" fontSize="11" fontWeight="bold">{d.date}</text>
                  <text x={x} y={Math.min(incomeY, expenseY) - 28} textAnchor="middle" fontSize="10" fill="#10b981">
                    +{new Intl.NumberFormat('ru-RU').format(d.income)} ₽
                  </text>
                  <text x={x} y={Math.min(incomeY, expenseY) - 16} textAnchor="middle" fontSize="10" fill="#ef4444">
                    -{new Intl.NumberFormat('ru-RU').format(d.expense)} ₽
                  </text>
                </g>
              )}

              {i % Math.ceil(data.length / 8) === 0 && (
                <text x={x} y={chartHeight - padding.bottom + 20} textAnchor="middle" fontSize="10" fill="#6b7280">{d.date}</text>
              )}
            </g>
          );
        })}
      </svg>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '3px', background: '#10b981' }}></div>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Доходы</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: '20px', height: '3px', background: '#ef4444' }}></div>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Расходы</span>
        </div>
      </div>
    </div>
  );
};

// Простая круговая диаграмма
const SimplePieChart: React.FC<{ data: Array<{ category: string; amount: number; percentage: number }>; colors: string[] }> = ({ data, colors }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const centerX = 140;
  const centerY = 140;
  const radius = 110;
  let currentAngle = -90;

  return (
    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
      <svg width="280" height="280" style={{ flexShrink: 0 }}>
        {data.map((item, i) => {
          const angle = (item.percentage / 100) * 360;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          currentAngle = endAngle;

          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          const x1 = centerX + radius * Math.cos(startRad);
          const y1 = centerY + radius * Math.sin(startRad);
          const x2 = centerX + radius * Math.cos(endRad);
          const y2 = centerY + radius * Math.sin(endRad);
          const largeArc = angle > 180 ? 1 : 0;
          const path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

          const labelAngle = (startAngle + endAngle) / 2;
          const labelRad = (labelAngle * Math.PI) / 180;
          const labelX = centerX + radius * 0.7 * Math.cos(labelRad);
          const labelY = centerY + radius * 0.7 * Math.sin(labelRad);

          return (
            <g key={i}>
              <path d={path} fill={colors[i % colors.length]} stroke="white" strokeWidth="2"
                onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}
                style={{ cursor: 'pointer', transform: hoveredIndex === i ? 'scale(1.05)' : 'scale(1)', transformOrigin: `${centerX}px ${centerY}px`, transition: 'transform 0.2s' }} />
              
              {item.percentage > 5 && (
                <text x={labelX} y={labelY} textAnchor="middle" fontSize="12" fontWeight="bold" fill="white" style={{ pointerEvents: 'none' }}>
                  {item.percentage.toFixed(0)}%
                </text>
              )}

              {hoveredIndex === i && (
                <g>
                  <rect x={centerX - 75} y={centerY - 35} width="150" height="45" fill="white" stroke="#d1d5db" rx="6" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.1))" />
                  <text x={centerX} y={centerY - 15} textAnchor="middle" fontSize="12" fontWeight="bold">{item.category}</text>
                  <text x={centerX} y={centerY} textAnchor="middle" fontSize="11" fill="#6b7280">
                    {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(item.amount)}
                  </text>
                  <text x={centerX} y={centerY + 13} textAnchor="middle" fontSize="10" fill="#9ca3af">{item.percentage.toFixed(1)}%</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      <div style={{ flex: 1, maxHeight: '280px', overflowY: 'auto' }}>
        {data.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '10px', padding: '6px', borderRadius: '4px',
            background: hoveredIndex === i ? '#f3f4f6' : 'transparent', cursor: 'pointer'
          }}
          onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}>
            <div style={{ width: '14px', height: '14px', borderRadius: '3px', background: colors[i % colors.length], flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: '12px' }}>
              <div style={{ fontWeight: '500', color: '#1f2937' }}>{item.category}</div>
              <div style={{ fontSize: '10px', color: '#6b7280' }}>
                {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(item.amount)}
              </div>
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>{item.percentage.toFixed(1)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Finances;
